import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Save, Plus, FileText, Image, Music, Mic, 
  Layers, Trash2, Play, Upload, Film, FolderOpen, Video, Search, LayoutGrid, Edit3, Wand2, Download, GripVertical, Users, ChevronLeft, Check, X, Loader2, Flag, Star, AlertTriangle
} from "lucide-react";
import SmartImportModal from "@/components/SmartImportModal";
import SmartImportSetsModal from "@/components/SmartImportSetsModal";
import SmartImportItemsModal from "@/components/SmartImportItemsModal";
import { uploadFileToS3 } from "@/utils/s3Upload";

import { getApiUrl, getApiBaseUrl } from '@/config/api';

const API = getApiUrl();

// Status configuration for shot list
const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-zinc-600" },
  ready: { label: "Ready", color: "bg-blue-600", hidden: true },
  animating: { label: "Animating", color: "bg-amber-500", spinner: true },
  animated: { label: "Animated", color: "bg-green-600" },
  needs_revision: { label: "Revision", color: "bg-orange-500" },
  final: { label: "Final", color: "bg-yellow-500", icon: Star }
};

const getAssetUrl = (fileUrl) => {
  if (!fileUrl) return '';
  const backendUrl = getApiBaseUrl();
  
  // Handle old absolute URLs - extract the path and use current backend
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    const apiMatch = fileUrl.match(/\/api\/(files|exports)\/(.+)/);
    if (apiMatch) {
      return `${backendUrl}/api/${apiMatch[1]}/${apiMatch[2]}`;
    }
    // Handle S3 URLs
    const s3Match = fileUrl.match(/https:\/\/([^.]+)\.s3(?:\.[^.]+)?\.amazonaws\.com\/([^?]+)/);
    if (s3Match) {
      return `${backendUrl}/api/s3-proxy/${s3Match[2]}`;
    }
    return fileUrl;
  }
  
  if (fileUrl.startsWith('/api/')) {
    return `${backendUrl}${fileUrl}`;
  }
  return `${backendUrl}/api/${fileUrl.replace(/^\//, '')}`;
};

const ASSET_TYPES = [
  { id: "background", label: "Sets", icon: Image, accept: "image/png,image/jpeg,image/webp" },
  { id: "character", label: "Characters", icon: Layers, accept: "image/png,image/jpeg,image/webp" },
  { id: "prop", label: "Items", icon: FolderOpen, accept: "image/png,image/jpeg,image/webp" },
  { id: "voice", label: "Voice Files", icon: Mic, accept: "audio/mpeg,audio/wav,audio/mp3,audio/x-wav" },
  { id: "music", label: "Music", icon: Music, accept: "audio/mpeg,audio/wav,audio/mp3,audio/x-wav" },
  { id: "motion", label: "Motion Refs", icon: Video, accept: "video/mp4,video/quicktime,video/webm" },
];

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();
  
  const [project, setProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [editingSceneName, setEditingSceneName] = useState("");
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [script, setScript] = useState("");
  const [activeAssetType, setActiveAssetType] = useState("background");
  const [newSceneDialog, setNewSceneDialog] = useState(false);
  const [newSceneName, setNewSceneName] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [assetSearch, setAssetSearch] = useState("");
  const [renamingAsset, setRenamingAsset] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const scriptRef = useRef(null);
  
  // Edit project description state
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  
  // File upload progress state
  const [fileUploadProgress, setFileUploadProgress] = useState(null); // { filename, progress, stage }
  
  // Add Shot state
  const [showAddShotChoice, setShowAddShotChoice] = useState(false);
  const [showCreateShotModal, setShowCreateShotModal] = useState(false);
  const [newShotName, setNewShotName] = useState("");
  const [newShotInsertIndex, setNewShotInsertIndex] = useState(null);
  const [creatingShotLoading, setCreatingShotLoading] = useState(false);
  
  // Import assets state
  const [importDialog, setImportDialog] = useState(false);
  const [importableProjects, setImportableProjects] = useState([]);
  const [selectedSourceProject, setSelectedSourceProject] = useState(null);
  const [sourceAssets, setSourceAssets] = useState([]);
  const [selectedImportAssets, setSelectedImportAssets] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Smart Import state for characters
  const [showSmartImport, setShowSmartImport] = useState(false);
  const [smartImportFiles, setSmartImportFiles] = useState([]);
  const [characterUploadProgress, setCharacterUploadProgress] = useState(null);
  
  // Smart Import state for sets/backgrounds
  const [showSmartImportSets, setShowSmartImportSets] = useState(false);
  const [smartImportSetFiles, setSmartImportSetFiles] = useState([]);
  
  // Smart Import state for items/props
  const [showSmartImportItems, setShowSmartImportItems] = useState(false);
  const [smartImportItemFiles, setSmartImportItemFiles] = useState([]);
  
  // Lazy loading state for assets
  const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
  const INITIAL_ASSET_LIMIT = 7;
  
  // Character Profiles state
  const [characterProfiles, setCharacterProfiles] = useState([]);
  const [selectedCharacterProfile, setSelectedCharacterProfile] = useState(null);
  
  // Scene drag-and-drop state
  const [draggedSceneIndex, setDraggedSceneIndex] = useState(null);
  const [dragOverSceneIndex, setDragOverSceneIndex] = useState(null);

  useEffect(() => {
    fetchProject();
    fetchScenes();
    fetchAssets();
    fetchCharacterProfiles();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProject(response.data);
      setScript(response.data.script || "");
    } catch (error) {
      toast.error("Failed to load project");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchScenes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/scenes`, {
        headers: getAuthHeaders()
      });
      // Sort by order field, then by scene_number, then by created_at
      const sortedScenes = (response.data || []).sort((a, b) => {
        // First sort by order if available
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Then by scene_number
        if (a.scene_number !== undefined && b.scene_number !== undefined) {
          return a.scene_number - b.scene_number;
        }
        // Finally by created_at
        return new Date(a.created_at) - new Date(b.created_at);
      });
      setScenes(sortedScenes);
    } catch (error) {
      console.error("Failed to load scenes");
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/assets`, {
        headers: getAuthHeaders()
      });
      setAssets(response.data);
    } catch (error) {
      console.error("Failed to load assets");
    }
  };

  const fetchCharacterProfiles = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/characters`, {
        headers: getAuthHeaders()
      });
      // Sort alphabetically
      const sorted = (response.data || []).sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      setCharacterProfiles(sorted);
    } catch (error) {
      console.error("Failed to load character profiles");
    }
  };

  const fetchImportableProjects = async () => {
    setImportLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/importable-projects`, {
        headers: getAuthHeaders()
      });
      setImportableProjects(response.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setImportLoading(false);
    }
  };

  const fetchSourceAssets = async (sourceProjectId) => {
    setImportLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${sourceProjectId}/exportable-assets`, {
        headers: getAuthHeaders()
      });
      setSourceAssets(response.data);
      setSelectedImportAssets([]);
    } catch (error) {
      toast.error("Failed to load assets");
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenImportDialog = () => {
    setImportDialog(true);
    setSelectedSourceProject(null);
    setSourceAssets([]);
    setSelectedImportAssets([]);
    fetchImportableProjects();
  };

  const handleSelectSourceProject = (proj) => {
    setSelectedSourceProject(proj);
    fetchSourceAssets(proj.id);
  };

  const toggleImportAsset = (assetId) => {
    setSelectedImportAssets(prev => {
      if (prev.includes(assetId)) {
        return prev.filter(id => id !== assetId);
      } else {
        return [...prev, assetId];
      }
    });
  };

  const selectAllImportAssets = () => {
    if (selectedImportAssets.length === sourceAssets.length) {
      setSelectedImportAssets([]);
    } else {
      setSelectedImportAssets(sourceAssets.map(a => a.id));
    }
  };

  const handleImportAssets = async (importAll = false) => {
    if (!selectedSourceProject) return;
    
    const assetIds = importAll ? "all" : selectedImportAssets;
    if (!importAll && selectedImportAssets.length === 0) {
      toast.error("Please select assets to import");
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/import-assets`, {
        source_project_id: selectedSourceProject.id,
        asset_ids: assetIds
      }, {
        headers: getAuthHeaders()
      });
      toast.success(`Imported ${response.data.imported_count} assets`);
      // Refresh both assets and character profiles
      fetchAssets();
      fetchCharacterProfiles();
      setImportDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import assets");
    } finally {
      setImporting(false);
    }
  };

  const handleSaveScript = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}/script`, { script }, {
        headers: getAuthHeaders()
      });
      toast.success("Script saved");
    } catch (error) {
      toast.error("Failed to save script");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDescription = async () => {
    if (descriptionValue === project?.description) {
      setEditingDescription(false);
      return;
    }
    
    setSavingDescription(true);
    try {
      await axios.put(`${API}/projects/${projectId}`, {
        name: project.name,
        description: descriptionValue
      }, {
        headers: getAuthHeaders()
      });
      setProject(prev => ({ ...prev, description: descriptionValue }));
      toast.success("Description updated");
      setEditingDescription(false);
    } catch (error) {
      toast.error("Failed to update description");
    } finally {
      setSavingDescription(false);
    }
  };

  const handleStartEditDescription = () => {
    setDescriptionValue(project?.description || "");
    setEditingDescription(true);
  };

  const handleTextSelect = () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    setSelectedText(text || "");
  };

  const handleCreateScene = async () => {
    if (!newSceneName.trim()) {
      toast.error("Please enter a scene name");
      return;
    }

    try {
      const response = await axios.post(`${API}/projects/${projectId}/scenes`, {
        name: newSceneName,
        script_segment: selectedText
      }, {
        headers: getAuthHeaders()
      });
      setScenes([...scenes, response.data]);
      setNewSceneDialog(false);
      setNewSceneName("");
      setSelectedText("");
      toast.success("Shot created! Open it to add characters and background.");
    } catch (error) {
      toast.error("Failed to create shot");
    }
  };

  const handleDeleteScene = async (sceneId) => {
    try {
      await axios.delete(`${API}/scenes/${sceneId}`);
      setScenes(scenes.filter(s => s.id !== sceneId));
      toast.success("Shot deleted");
    } catch (error) {
      toast.error("Failed to delete shot");
    }
  };

  const handleSaveSceneName = async (sceneId, newName) => {
    if (!newName.trim()) {
      toast.error("Shot name cannot be empty");
      return;
    }
    try {
      await axios.put(`${API}/scenes/${sceneId}`, { name: newName.trim() }, { headers: getAuthHeaders() });
      setScenes(scenes.map(s => s.id === sceneId ? { ...s, name: newName.trim() } : s));
      toast.success("Shot name updated");
    } catch (error) {
      toast.error("Failed to update shot name");
    }
    setEditingSceneId(null);
    setEditingSceneName("");
  };

  const handleCreateShot = async () => {
    if (!newShotName.trim()) {
      toast.error("Please enter a shot name");
      return;
    }
    if (newShotInsertIndex === null) {
      toast.error("Please select where to insert the shot");
      return;
    }
    
    try {
      setCreatingShotLoading(true);
      
      // Calculate the order value based on insertion index
      const sortedScenes = [...scenes].sort((a, b) => (a.order || 0) - (b.order || 0));
      let insertOrder;
      
      if (sortedScenes.length === 0) {
        insertOrder = 1;
      } else if (newShotInsertIndex === 0) {
        // Before first shot
        const firstOrder = sortedScenes[0]?.order || 1;
        insertOrder = firstOrder - 0.5;
      } else if (newShotInsertIndex >= sortedScenes.length) {
        // After last shot
        const lastOrder = sortedScenes[sortedScenes.length - 1]?.order || sortedScenes.length;
        insertOrder = lastOrder + 1;
      } else {
        // Between two shots
        const prevOrder = sortedScenes[newShotInsertIndex - 1]?.order || newShotInsertIndex;
        const nextOrder = sortedScenes[newShotInsertIndex]?.order || newShotInsertIndex + 1;
        insertOrder = (prevOrder + nextOrder) / 2;
      }
      
      const response = await axios.post(
        `${API}/projects/${projectId}/scenes`,
        { 
          name: newShotName.trim(),
          order: insertOrder,
          script_segment: ""
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data) {
        const newScene = response.data;
        setScenes(prev => [...prev, newScene].sort((a, b) => (a.order || 0) - (b.order || 0)));
        toast.success(`Created "${newShotName}"`);
        setShowCreateShotModal(false);
        setNewShotName("");
        setNewShotInsertIndex(null);
        
        // Navigate to the new shot
        navigate(`/project/${projectId}/studio/${newScene.id}`);
      }
    } catch (err) {
      console.error('Create shot failed:', err);
      toast.error('Failed to create shot: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreatingShotLoading(false);
    }
  };

  // Scene drag-and-drop handlers
  const handleSceneDragStart = (e, index) => {
    setDraggedSceneIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleSceneDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverSceneIndex !== index) {
      setDragOverSceneIndex(index);
    }
  };

  const handleSceneDragLeave = () => {
    setDragOverSceneIndex(null);
  };

  const handleSceneDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverSceneIndex(null);
    
    if (draggedSceneIndex === null || draggedSceneIndex === dropIndex) {
      setDraggedSceneIndex(null);
      return;
    }

    // Reorder scenes locally
    const newScenes = [...scenes];
    const [draggedScene] = newScenes.splice(draggedSceneIndex, 1);
    newScenes.splice(dropIndex, 0, draggedScene);
    setScenes(newScenes);
    setDraggedSceneIndex(null);

    // Save new order to backend
    try {
      const sceneIds = newScenes.map(s => s.id);
      await axios.put(`${API}/projects/${projectId}/scenes/reorder`, sceneIds);
      toast.success("Shot order updated");
    } catch (error) {
      // Revert on error
      fetchScenes();
      toast.error("Failed to reorder scenes");
    }
  };

  const handleSceneDragEnd = () => {
    setDraggedSceneIndex(null);
    setDragOverSceneIndex(null);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // For character uploads, use Smart Import
    if (activeAssetType === "character") {
      setSmartImportFiles(files);
      setShowSmartImport(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // For background/set uploads with multiple files, use Smart Import Sets
    if (activeAssetType === "background" && files.length > 1) {
      setSmartImportSetFiles(files);
      setShowSmartImportSets(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // For item/prop uploads with multiple files, use Smart Import Items
    if (activeAssetType === "prop" && files.length > 1) {
      setSmartImportItemFiles(files);
      setShowSmartImportItems(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // For other asset types or single files, upload with appropriate method
    // Use S3 direct upload for larger files (audio, video, motion refs) to avoid ingress limits
    const useS3Upload = ['music', 'sfx', 'voice', 'video', 'motion'].includes(activeAssetType);
    
    for (const file of files) {
      try {
        // Show upload progress
        setFileUploadProgress({ filename: file.name, progress: 0, stage: 'uploading' });
        
        let newAsset;
        
        if (useS3Upload) {
          // Use S3 direct upload for large files (bypasses ingress body size limits)
          newAsset = await uploadFileToS3(file, projectId, {
            type: activeAssetType,
            name: file.name.replace(/\.[^/.]+$/, ""),
            onProgress: (progress) => {
              setFileUploadProgress(prev => ({
                ...prev,
                progress,
                stage: progress < 100 ? 'uploading' : 'processing'
              }));
            }
          });
        } else {
          // Use direct FormData upload for smaller files (images, backgrounds)
          const formData = new FormData();
          formData.append("file", file);
          formData.append("type", activeAssetType);
          formData.append("name", file.name.replace(/\.[^/.]+$/, ""));

          const response = await axios.post(
            `${API}/projects/${projectId}/assets`,
            formData,
            {
              onUploadProgress: (progressEvent) => {
                const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                setFileUploadProgress(prev => ({
                  ...prev,
                  progress,
                  stage: progress < 100 ? 'uploading' : 'processing'
                }));
              }
            }
          );
          newAsset = response.data;
        }
        
        // Add the new asset to state immediately
        setAssets(prev => [...prev, newAsset]);
        setFileUploadProgress(null);
        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        setFileUploadProgress(null);
        console.error(`Upload error for ${file.name}:`, error);
        
        // Provide specific error messages
        let errorMessage = `Failed to upload ${file.name}`;
        if (error.response?.status === 413) {
          errorMessage = `File too large: ${file.name}. Max size is 50MB.`;
        } else if (error.response?.status === 415) {
          errorMessage = `Unsupported format: ${file.name}`;
        } else if (error.response?.status === 520) {
          errorMessage = `Server error uploading ${file.name}. Please try again.`;
        } else if (error.message?.includes('network') || error.code === 'ERR_NETWORK') {
          errorMessage = `Network error uploading ${file.name}. Check your connection.`;
        } else if (error.response?.data?.detail) {
          errorMessage = `${file.name}: ${error.response.data.detail}`;
        }
        toast.error(errorMessage);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Smart Import handler for characters - Uses direct S3 upload with throttling
  const handleSmartImportConfirm = async (importData) => {
    let created = 0;
    let updated = 0;
    let imagesAdded = 0;
    const totalFiles = importData.reduce((sum, group) => sum + group.files.length, 0);
    let processedFiles = 0;
    
    // Initialize progress
    setCharacterUploadProgress({ current: 0, total: totalFiles, currentFile: '' });
    
    // Update progress in the modal
    const updateProgress = (filename) => {
      processedFiles++;
      setCharacterUploadProgress({ 
        current: processedFiles, 
        total: totalFiles, 
        currentFile: filename 
      });
    };
    
    // Helper function to upload a single file via S3 with retry logic
    const uploadFileViaS3 = async (file, characterId, retries = 3) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          // Step 1: Get presigned URL for direct S3 upload
          const presignedResponse = await axios.post(
            `${API}/uploads/get-presigned-url`,
            {
              filename: file.name,
              content_type: file.type,
              project_id: projectId
            },
            { headers: getAuthHeaders() }
          );
          
          const { upload_url, s3_key } = presignedResponse.data;
          
          // Step 2: Upload directly to S3 (bypasses backend/ingress limits)
          await axios.put(upload_url, file, {
            headers: {
              'Content-Type': file.type
            }
          });
          
          // Step 3: Create asset record in database (skip_thumbnail=true for fast bulk uploads)
          const assetResponse = await axios.post(
            `${API}/projects/${projectId}/assets/from-s3`,
            {
              name: file.name.replace(/\.[^/.]+$/, ""),
              type: "character",
              s3_key: s3_key,
              character_profile_id: characterId,
              skip_thumbnail: true
            },
            { headers: getAuthHeaders() }
          );
          
          return assetResponse.data;
        } catch (error) {
          console.warn(`Attempt ${attempt}/${retries} failed for ${file.name}:`, error.message);
          if (attempt < retries) {
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          } else {
            throw error;
          }
        }
      }
    };
    
    for (const group of importData) {
      try {
        let characterId = group.existingCharacterId;
        
        // Create new character if doesn't exist
        if (!characterId) {
          const createResponse = await axios.post(`${API}/projects/${projectId}/characters`, {
            name: group.name,
            description: ""
          }, { headers: getAuthHeaders() });
          characterId = createResponse.data.id;
          created++;
        } else {
          updated++;
        }
        
        // Upload each file via S3 presigned URLs (bypasses server for file upload)
        for (const file of group.files) {
          try {
            const result = await uploadFileViaS3(file, characterId);
            setAssets(prev => [...prev, result]);
            imagesAdded++;
          } catch (error) {
            console.error(`Failed to upload ${file.name}:`, error);
            toast.error(`Failed to upload ${file.name}`);
          }
          
          updateProgress(file.name);
          
          // Delay between uploads to prevent server overload
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error(`Failed to import ${group.name}:`, error);
        toast.error(`Failed to import ${group.name}`);
      }
    }
    
    // Clear progress and refresh character profiles
    setCharacterUploadProgress(null);
    await fetchCharacterProfiles();
    
    const messages = [];
    if (created > 0) messages.push(`${created} new character${created !== 1 ? 's' : ''}`);
    if (updated > 0) messages.push(`${updated} updated`);
    messages.push(`${imagesAdded} image${imagesAdded !== 1 ? 's' : ''} added`);
    
    toast.success(`Import complete: ${messages.join(', ')}`);
    
    setShowSmartImport(false);
    setSmartImportFiles([]);
  };

  // Smart Import handler for sets/backgrounds - Uses direct S3 upload with throttling
  const handleSmartImportSetsConfirm = async (groups, onProgress) => {
    let totalUploaded = 0;
    let totalGroups = Object.keys(groups).length;
    const totalFiles = Object.values(groups).reduce((sum, items) => sum + items.length, 0);
    
    // Helper function with retry logic
    const uploadWithRetry = async (item, locationName, retries = 3) => {
      const baseName = item.originalName.replace(/\.[^/.]+$/, "");
      const assetName = `${locationName} - ${baseName}`;
      
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          // Step 1: Get presigned URL for direct S3 upload
          const presignedResponse = await axios.post(
            `${API}/uploads/get-presigned-url`,
            {
              filename: item.originalName,
              content_type: item.file.type,
              project_id: projectId
            },
            { headers: getAuthHeaders() }
          );
          
          const { upload_url, file_url, s3_key } = presignedResponse.data;
          
          // Step 2: Upload directly to S3 (bypasses backend/ingress limits)
          await axios.put(upload_url, item.file, {
            headers: {
              'Content-Type': item.file.type
            }
          });
          
          // Step 3: Create asset record in database (skip_thumbnail=true for fast bulk uploads)
          const assetResponse = await axios.post(
            `${API}/projects/${projectId}/assets/from-s3`,
            {
              name: assetName,
              type: "background",
              s3_key: s3_key,
              file_url: file_url,
              folder: locationName,
              skip_thumbnail: true  // Skip thumbnail generation to prevent 520 timeouts
            },
            { headers: getAuthHeaders() }
          );
          
          return assetResponse.data;
        } catch (error) {
          console.warn(`Attempt ${attempt}/${retries} failed for ${item.originalName}:`, error.message);
          if (attempt < retries) {
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          } else {
            throw error;
          }
        }
      }
    };
    
    // Process uploads ONE AT A TIME to prevent 520 timeouts
    // Cloudflare/ingress can't handle concurrent requests well
    const allItems = [];
    for (const [locationName, items] of Object.entries(groups)) {
      for (const item of items) {
        allItems.push({ item, locationName });
      }
    }
    
    let processedCount = 0;
    for (const { item, locationName } of allItems) {
      try {
        const result = await uploadWithRetry(item, locationName);
        setAssets(prev => [...prev, result]);
        totalUploaded++;
      } catch (error) {
        console.error('Upload failed:', error);
      }
      
      processedCount++;
      if (onProgress) {
        onProgress(processedCount, totalFiles, item?.originalName || '');
      }
      
      // Delay between each upload to prevent server overload
      if (processedCount < allItems.length) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    toast.success(`Imported ${totalUploaded}/${totalFiles} backgrounds in ${totalGroups} location${totalGroups !== 1 ? 's' : ''}`);
    
    // Trigger background thumbnail generation for uploaded assets
    try {
      await axios.post(
        `${API}/projects/${projectId}/generate-thumbnails`,
        {},
        { headers: getAuthHeaders() }
      );
      console.log('Thumbnail generation started in background');
    } catch (thumbError) {
      console.warn('Failed to start thumbnail generation:', thumbError);
    }
    
    setShowSmartImportSets(false);
    setSmartImportSetFiles([]);
  };

  // Smart Import handler for items/props - Uses direct S3 upload with throttling
  const handleSmartImportItemsConfirm = async (groups, onProgress) => {
    let totalUploaded = 0;
    let totalGroups = Object.keys(groups).length;
    const totalFiles = Object.values(groups).reduce((sum, items) => sum + items.length, 0);
    
    // Helper function with retry logic
    const uploadWithRetry = async (item, itemName, retries = 3) => {
      const baseName = item.originalName.replace(/\.[^/.]+$/, "");
      const assetName = `${itemName} - ${baseName}`;
      
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          // Step 1: Get presigned URL for direct S3 upload
          const presignedResponse = await axios.post(
            `${API}/uploads/get-presigned-url`,
            {
              filename: item.originalName,
              content_type: item.file.type,
              project_id: projectId
            },
            { headers: getAuthHeaders() }
          );
          
          const { upload_url, file_url, s3_key } = presignedResponse.data;
          
          // Step 2: Upload directly to S3 (bypasses backend/ingress limits)
          await axios.put(upload_url, item.file, {
            headers: {
              'Content-Type': item.file.type
            }
          });
          
          // Step 3: Create asset record in database (skip_thumbnail=true for fast bulk uploads)
          const assetResponse = await axios.post(
            `${API}/projects/${projectId}/assets/from-s3`,
            {
              name: assetName,
              type: "prop",
              s3_key: s3_key,
              file_url: file_url,
              folder: itemName,
              skip_thumbnail: true  // Skip thumbnail generation to prevent 520 timeouts
            },
            { headers: getAuthHeaders() }
          );
          
          return assetResponse.data;
        } catch (error) {
          console.warn(`Attempt ${attempt}/${retries} failed for ${item.originalName}:`, error.message);
          if (attempt < retries) {
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          } else {
            throw error;
          }
        }
      }
    };
    
    // Process uploads ONE AT A TIME to prevent 520 timeouts
    const allItems = [];
    for (const [itemName, items] of Object.entries(groups)) {
      for (const item of items) {
        allItems.push({ item, itemName });
      }
    }
    
    let processedCount = 0;
    for (const { item, itemName } of allItems) {
      try {
        const result = await uploadWithRetry(item, itemName);
        setAssets(prev => [...prev, result]);
        totalUploaded++;
      } catch (error) {
        console.error('Upload failed:', error);
      }
      
      processedCount++;
      if (onProgress) {
        onProgress(processedCount, totalFiles, item?.originalName || '');
      }
      
      // Delay between each upload to prevent server overload
      if (processedCount < allItems.length) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    toast.success(`Imported ${totalUploaded}/${totalFiles} items in ${totalGroups} group${totalGroups !== 1 ? 's' : ''}`);
    
    // Trigger background thumbnail generation for uploaded assets
    try {
      await axios.post(
        `${API}/projects/${projectId}/generate-thumbnails`,
        {},
        { headers: getAuthHeaders() }
      );
      console.log('Thumbnail generation started in background');
    } catch (thumbError) {
      console.warn('Failed to start thumbnail generation:', thumbError);
    }
    
    setShowSmartImportItems(false);
    setSmartImportItemFiles([]);
  };

  const handleDeleteAsset = async (assetId, e) => {
    e?.stopPropagation();
    try {
      await axios.delete(`${API}/assets/${assetId}`);
      setAssets(assets.filter(a => a.id !== assetId));
      toast.success("Asset deleted");
    } catch (error) {
      toast.error("Failed to delete asset");
    }
  };

  const handleRenameAsset = async (assetId) => {
    if (!renameValue.trim()) {
      toast.error("Please enter a name");
      return;
    }
    try {
      await axios.patch(`${API}/assets/${assetId}`, { name: renameValue });
      setAssets(assets.map(a => a.id === assetId ? { ...a, name: renameValue } : a));
      setRenamingAsset(null);
      setRenameValue("");
      toast.success("Asset renamed");
    } catch (error) {
      toast.error("Failed to rename asset");
    }
  };

  const getAssetsByType = (type) => {
    let filtered = assets.filter(a => a.type === type);
    if (assetSearch.trim()) {
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(assetSearch.toLowerCase())
      );
    }
    return filtered;
  };

  const currentAssetType = ASSET_TYPES.find(t => t.id === activeAssetType);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] animate-fadeIn">
      {/* Smart Import Modal for characters */}
      <SmartImportModal
        open={showSmartImport}
        onClose={() => {
          setShowSmartImport(false);
          setSmartImportFiles([]);
          setCharacterUploadProgress(null);
        }}
        files={smartImportFiles}
        existingCharacters={characterProfiles}
        onConfirm={handleSmartImportConfirm}
        projectId={projectId}
        uploadProgress={characterUploadProgress}
      />

      {/* Smart Import for Sets/Backgrounds */}
      <SmartImportSetsModal
        open={showSmartImportSets}
        onClose={() => {
          setShowSmartImportSets(false);
          setSmartImportSetFiles([]);
        }}
        files={smartImportSetFiles}
        onConfirm={handleSmartImportSetsConfirm}
        projectId={projectId}
      />

      {/* Smart Import for Items/Props */}
      <SmartImportItemsModal
        open={showSmartImportItems}
        onClose={() => {
          setShowSmartImportItems(false);
          setSmartImportItemFiles([]);
        }}
        files={smartImportItemFiles}
        onConfirm={handleSmartImportItemsConfirm}
        projectId={projectId}
      />
      
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-white">{project?.name}</h1>
              {editingDescription ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={descriptionValue}
                    onChange={(e) => setDescriptionValue(e.target.value)}
                    placeholder="Add a project description..."
                    className="h-7 text-sm bg-zinc-800 border-zinc-700 text-white w-64"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveDescription();
                      if (e.key === 'Escape') setEditingDescription(false);
                    }}
                    data-testid="description-input"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleSaveDescription}
                    disabled={savingDescription}
                    className="h-7 w-7 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                    data-testid="save-description-btn"
                  >
                    {savingDescription ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setEditingDescription(false)}
                    className="h-7 w-7 text-zinc-400 hover:text-white hover:bg-zinc-700"
                    data-testid="cancel-description-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="flex items-center gap-2 group cursor-pointer"
                  onClick={handleStartEditDescription}
                  data-testid="description-display"
                >
                  <p className="text-sm text-zinc-400 group-hover:text-zinc-300">
                    {project?.description || "Add a description..."}
                  </p>
                  <Edit3 className="w-3 h-3 text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/project/${projectId}/characters`)}
              className="border-zinc-700 text-zinc-300 hover:text-white gap-2"
              data-testid="characters-btn"
            >
              <Users className="w-4 h-4" />
              Characters
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/project/${projectId}/team`)}
              className="border-zinc-700 text-zinc-300 hover:text-white gap-2"
              data-testid="team-btn"
            >
              <Users className="w-4 h-4" />
              Cast & Crew
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/project/${projectId}/editor`)}
              className="border-zinc-700 text-zinc-300 hover:text-white gap-2"
              data-testid="project-editor-btn"
            >
              <Film className="w-4 h-4" />
              Project Editor
            </Button>
            <Button
              onClick={handleSaveScript}
              disabled={saving}
              className="bg-indigo-500 hover:bg-indigo-600 gap-2"
              data-testid="save-script-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - 3 Column Layout */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Column 1: Asset Library */}
          <div className="lg:col-span-3">
            <Card className="bg-zinc-900/50 border-zinc-800 h-[calc(100vh-180px)] flex flex-col">
              <CardHeader className="border-b border-zinc-800 pb-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-indigo-400" />
                  <CardTitle className="text-white text-lg">Asset Library</CardTitle>
                </div>
              </CardHeader>
              
              {/* Tabs Header - Fixed at top */}
              <div className="flex-shrink-0 px-3 pt-3">
                <div className="flex flex-wrap gap-1 bg-zinc-800 rounded-lg p-1">
                  {ASSET_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => { setActiveAssetType(type.id); setAssetSearch(""); setSelectedCharacterProfile(null); }}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                        activeAssetType === type.id 
                          ? 'bg-indigo-500 text-white' 
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                      }`}
                      data-testid={`tab-${type.id}`}
                    >
                      <type.icon className="w-3 h-3" />
                      <span className="hidden sm:inline">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Search & Actions - Fixed below tabs */}
              <div className="flex-shrink-0 px-3 py-3 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder={`Search ${ASSET_TYPES.find(t => t.id === activeAssetType)?.label.toLowerCase() || 'assets'}...`}
                    value={assetSearch}
                    onChange={(e) => setAssetSearch(e.target.value)}
                    className="pl-8 bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                    data-testid={`search-${activeAssetType}`}
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ASSET_TYPES.find(t => t.id === activeAssetType)?.accept}
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid={`file-input-${activeAssetType}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-indigo-500 h-8"
                    data-testid={`upload-${activeAssetType}-btn`}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenImportDialog}
                    className="flex-1 border-zinc-700 text-zinc-400 hover:text-white hover:border-indigo-500 h-8"
                    data-testid="import-assets-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Import
                  </Button>
                </div>
              </div>
              
              {/* Scrollable Asset Grid */}
              <div className="flex-1 overflow-y-auto px-3 pb-3 min-h-0">
                {activeAssetType === "character" ? (
                  // Character Profiles View
                  selectedCharacterProfile ? (
                    // Inside a character profile - show poses
                    <div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedCharacterProfile(null)}
                        className="mb-2 text-zinc-400 hover:text-white -ml-1"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back to Characters
                      </Button>
                      <div className="text-sm font-medium text-white mb-2">{selectedCharacterProfile.name}</div>
                      {selectedCharacterProfile.reference_images?.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500 text-sm">
                          No images in this character profile
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {selectedCharacterProfile.reference_images?.map((img, index) => {
                            const asset = assets.find(a => a.id === img.asset_id);
                            if (!asset) return null;
                            return (
                              <div
                                key={img.asset_id}
                                data-testid={`character-pose-${index}`}
                                className={`relative group aspect-square rounded-lg bg-zinc-800 overflow-hidden border ${img.is_primary ? 'border-amber-500' : 'border-zinc-700'}`}
                              >
                                <img
                                  src={getAssetUrl(img.file_url || asset.file_url)}
                                  alt={asset.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                                {img.is_primary && (
                                  <div className="absolute top-1 left-1 bg-amber-500 text-black text-[10px] px-1 py-0.5 rounded font-medium">
                                    Primary
                                  </div>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-4">
                                  <span className="text-xs text-white truncate block font-medium">{asset.name}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Character profiles list (folder view)
                    characterProfiles.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500 text-sm">
                        {assetSearch ? `No characters matching "${assetSearch}"` : "No characters uploaded"}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {characterProfiles
                          .filter(profile => !assetSearch || profile.name.toLowerCase().includes(assetSearch.toLowerCase()))
                          .map((profile, index) => {
                            const primaryImage = profile.reference_images?.find(img => img.is_primary) || profile.reference_images?.[0];
                            return (
                              <div
                                key={profile.id}
                                onClick={() => setSelectedCharacterProfile(profile)}
                                data-testid={`character-profile-${index}`}
                                className="relative group aspect-square rounded-lg bg-zinc-800 overflow-hidden border border-zinc-700 cursor-pointer hover:border-indigo-500 transition-colors"
                              >
                                {primaryImage ? (
                                  <img
                                    src={getAssetUrl(primaryImage.thumbnail_url || primaryImage.file_url)}
                                    alt={profile.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Users className="w-8 h-8 text-zinc-600" />
                                  </div>
                                )}
                                <div className="absolute top-1 right-1 bg-black/60 text-zinc-300 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                                  <Layers className="w-3 h-3" />
                                  {profile.reference_images?.length || 0}
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-4">
                                  <span className="text-xs text-white truncate block font-medium">{profile.name}</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )
                  )
                ) : (
                  // Non-character assets - lazy loading grid view
                  (() => {
                    const currentType = ASSET_TYPES.find(t => t.id === activeAssetType);
                    const allTypeAssets = getAssetsByType(activeAssetType);
                    const isExpanded = expandedAssetTypes[activeAssetType] || false;
                    const isSearching = assetSearch.trim().length > 0;
                    const visibleAssets = isSearching || isExpanded 
                      ? allTypeAssets 
                      : allTypeAssets.slice(0, INITIAL_ASSET_LIMIT);
                    const hasMore = !isSearching && !isExpanded && allTypeAssets.length > INITIAL_ASSET_LIMIT;
                    const remainingCount = allTypeAssets.length - INITIAL_ASSET_LIMIT;
                    
                    if (allTypeAssets.length === 0) {
                      return (
                        <div className="text-center py-8 text-zinc-500 text-sm">
                          {assetSearch ? `No ${currentType?.label.toLowerCase()} matching "${assetSearch}"` : `No ${currentType?.label.toLowerCase()} uploaded`}
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        <div className="grid grid-cols-3 gap-1.5">
                          {visibleAssets.map((asset, index) => (
                            <div
                              key={asset.id}
                              data-testid={`asset-${activeAssetType}-${index}`}
                              className="relative group aspect-square rounded-md bg-zinc-800 overflow-hidden border border-zinc-700 hover:border-zinc-500 transition-colors"
                            >
                              {activeAssetType === "voice" || activeAssetType === "music" || activeAssetType === "motion" ? (
                                <div className="w-full h-full flex flex-col items-center justify-center p-1.5">
                                  {currentType && <currentType.icon className="w-5 h-5 text-zinc-400" />}
                                </div>
                              ) : (
                                <img
                                  src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                                  alt={asset.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              )}
                              
                              {/* Asset name overlay */}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 pt-3">
                                {renamingAsset === asset.id ? (
                                  <div className="flex items-center gap-0.5">
                                    <Input
                                      value={renameValue}
                                      onChange={(e) => setRenameValue(e.target.value)}
                                      onKeyDown={(e) => e.key === 'Enter' && handleRenameAsset(asset.id)}
                                      className="h-4 text-[10px] bg-zinc-900 border-zinc-600 text-white px-1"
                                      autoFocus
                                    />
                                    <Button size="icon" variant="ghost" onClick={() => handleRenameAsset(asset.id)} className="w-4 h-4 text-green-400 p-0">
                                      <Save className="w-2.5 h-2.5" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span 
                                    className="text-[10px] text-white truncate block font-medium cursor-pointer hover:text-indigo-300"
                                    onClick={(e) => { e.stopPropagation(); setRenamingAsset(asset.id); setRenameValue(asset.name); }}
                                    title={asset.name}
                                  >
                                    {asset.name}
                                  </span>
                                )}
                              </div>
                              
                              {/* Action buttons - on hover */}
                              <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => { e.stopPropagation(); setRenamingAsset(asset.id); setRenameValue(asset.name); }}
                                  className="w-5 h-5 bg-black/60 text-zinc-400 hover:text-white p-0"
                                  data-testid={`rename-asset-${activeAssetType}-${index}`}
                                >
                                  <Edit3 className="w-2.5 h-2.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => handleDeleteAsset(asset.id, e)}
                                  className="w-5 h-5 bg-black/60 text-zinc-400 hover:text-red-400 p-0"
                                  data-testid={`delete-asset-${activeAssetType}-${index}`}
                                >
                                  <Trash2 className="w-2.5 h-2.5" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Show More / Show Less buttons */}
                        {hasMore && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedAssetTypes(prev => ({ ...prev, [activeAssetType]: true }))}
                            className="w-full mt-2 text-xs text-zinc-400 hover:text-white h-7"
                          >
                            Show {remainingCount} more
                          </Button>
                        )}
                        {isExpanded && allTypeAssets.length > INITIAL_ASSET_LIMIT && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedAssetTypes(prev => ({ ...prev, [activeAssetType]: false }))}
                            className="w-full mt-2 text-xs text-zinc-400 hover:text-white h-7"
                          >
                            Show less
                          </Button>
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            </Card>
          </div>

          {/* Column 2: Script Editor */}
          <div className="lg:col-span-5">
            <Card className="bg-zinc-900/50 border-zinc-800 h-[calc(100vh-180px)]">
              <CardHeader className="border-b border-zinc-800 pb-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-400" />
                    <CardTitle className="text-white text-lg">Script Editor</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Script Tools Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/project/${projectId}/script-tools`)}
                      className="border-zinc-700 text-zinc-300 hover:text-white gap-1 text-xs"
                      data-testid="script-tools-btn"
                    >
                      <Wand2 className="w-4 h-4" />
                      Script Tools
                    </Button>
                    
                    {/* Create Shot Button - only visible when text is selected */}
                    {selectedText && (
                      <Dialog open={newSceneDialog} onOpenChange={setNewSceneDialog}>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-black gap-1 text-xs"
                            data-testid="create-scene-btn"
                          >
                            <Plus className="w-4 h-4" />
                            Create Shot from Selection
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-zinc-800">
                          <DialogHeader>
                            <DialogTitle className="text-white">Create New Shot</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label className="text-zinc-300">Shot Name</Label>
                              <Input
                                data-testid="scene-name-input"
                                value={newSceneName}
                                onChange={(e) => setNewSceneName(e.target.value)}
                                placeholder="Shot 1"
                                className="bg-zinc-800 border-zinc-700 text-white"
                              />
                            </div>
                            {selectedText && (
                              <div className="space-y-2">
                                <Label className="text-zinc-300">Selected Script</Label>
                                <div className="bg-zinc-800 border border-zinc-700 rounded-md p-3 text-sm text-zinc-300 font-mono max-h-32 overflow-y-auto">
                                  {selectedText}
                                </div>
                              </div>
                            )}
                          </div>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button variant="ghost" className="text-zinc-400">Cancel</Button>
                            </DialogClose>
                            <Button 
                              data-testid="confirm-create-scene-btn"
                              onClick={handleCreateScene}
                              className="bg-indigo-500 hover:bg-indigo-600"
                            >
                              Create Shot
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  Highlight text to create a shot, or use Script Tools for automation
                </p>
              </CardHeader>
              <CardContent className="p-0 h-[calc(100%-90px)]">
                <textarea
                  ref={scriptRef}
                  data-testid="script-textarea"
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  onMouseUp={handleTextSelect}
                  onKeyUp={handleTextSelect}
                  placeholder="Paste or write your script here...

SCENE 1: INT. HAUNTED MANSION - NIGHT

The team enters the dusty foyer. Cobwebs hang from the chandelier.

PETER: (nervously) Did anyone else hear that?

EGON: My PKE meter is off the charts. There's definitely something here.

A ghostly figure appears at the top of the stairs..."
                  className="script-editor w-full h-full p-4 text-zinc-200 placeholder:text-zinc-600 text-sm resize-none"
                />
              </CardContent>
            </Card>
          </div>

          {/* Column 3: Shots */}
          <div className="lg:col-span-4">
            <Card className="bg-zinc-900/50 border-zinc-800 h-[calc(100vh-180px)]">
              <CardHeader className="border-b border-zinc-800 pb-3">
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Film className="w-5 h-5 text-indigo-400" />
                    <CardTitle className="text-white text-lg">Shot List</CardTitle>
                    <span className="text-xs text-zinc-500">({scenes.length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddShotChoice(true)}
                      className="text-amber-400 border-amber-500/50 hover:bg-amber-500/10 hover:text-amber-300 gap-1"
                      data-testid="add-shot-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Shot
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/project/${projectId}/storyboard`)}
                      className="text-zinc-400 hover:text-white gap-1"
                    >
                      <LayoutGrid className="w-4 h-4" />
                      View Storyboard
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 h-[calc(100%-60px)]">
                {scenes.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Film className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                    <p>No scenes yet</p>
                    <p className="text-sm mt-1">Highlight script text to create scenes</p>
                  </div>
                ) : (
                  <ScrollArea className="h-full">
                    <div className="space-y-2">
                      {scenes.map((scene, index) => (
                        <div
                          key={scene.id}
                          data-testid={`scene-item-${index}`}
                          draggable
                          onDragStart={(e) => handleSceneDragStart(e, index)}
                          onDragOver={(e) => handleSceneDragOver(e, index)}
                          onDragLeave={handleSceneDragLeave}
                          onDrop={(e) => handleSceneDrop(e, index)}
                          onDragEnd={handleSceneDragEnd}
                          className={`group p-2 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-all cursor-grab active:cursor-grabbing ${
                            draggedSceneIndex === index ? 'opacity-50 scale-95' : ''
                          } ${
                            dragOverSceneIndex === index && draggedSceneIndex !== index
                              ? 'border-2 border-indigo-500 border-dashed'
                              : 'border-2 border-transparent'
                          }`}
                        >
                          {/* Top row: drag handle + thumbnail + info */}
                          <div className="flex items-center gap-2">
                            {/* Drag handle */}
                            <div className="text-zinc-600 hover:text-zinc-400 flex-shrink-0 cursor-grab">
                              <GripVertical className="w-3 h-3" />
                            </div>
                            
                            {/* Scene thumbnail */}
                            <div className="w-12 h-8 bg-zinc-700 rounded overflow-hidden flex-shrink-0 flex items-center justify-center relative">
                              {(() => {
                                // Priority: 1. Video first frame, 2. Composite, 3. Preview/Canvas, 4. Blank
                                const hasPrintedVideo = scene.status === 'final' || scene.printed_take_id;
                                const thumbnailUrl = hasPrintedVideo && scene.thumbnail_url 
                                  ? scene.thumbnail_url 
                                  : scene.composite_image_url || scene.preview_url || scene.thumbnail_url;
                                const isDraft = !hasPrintedVideo && thumbnailUrl;
                                
                                if (thumbnailUrl) {
                                  return (
                                    <>
                                      <img
                                        src={getAssetUrl(thumbnailUrl)}
                                        alt={scene.name}
                                        className={`w-full h-full object-cover ${isDraft ? 'saturate-50 opacity-80' : ''}`}
                                      />
                                      {isDraft && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-zinc-800/80 text-[6px] text-center text-zinc-400 py-0.5">
                                          Draft
                                        </div>
                                      )}
                                    </>
                                  );
                                }
                                return <Film className="w-3 h-3 text-zinc-500" />;
                              })()}
                              {/* Play button overlay on thumbnail */}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); navigate(`/project/${projectId}/studio/${scene.id}`); }}
                                className="absolute inset-0 w-full h-full bg-black/40 text-white hover:text-orange-400 hover:bg-black/50 transition-colors flex items-center justify-center rounded"
                                data-testid={`edit-scene-${index}`}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <span className="w-4 h-4 rounded bg-indigo-500/20 text-indigo-400 text-[10px] flex items-center justify-center font-medium flex-shrink-0">
                                  {index + 1}
                                </span>
                                {editingSceneId === scene.id ? (
                                  <div className="flex items-center gap-1 flex-1 min-w-0">
                                    <input
                                      type="text"
                                      value={editingSceneName}
                                      onChange={(e) => setEditingSceneName(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-white font-medium text-xs bg-zinc-700 border border-zinc-600 rounded px-1 py-0.5 flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') {
                                          handleSaveSceneName(scene.id, editingSceneName);
                                        } else if (e.key === 'Escape') {
                                          setEditingSceneId(null);
                                          setEditingSceneName("");
                                        }
                                      }}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => { e.stopPropagation(); handleSaveSceneName(scene.id, editingSceneName); }}
                                      className="w-5 h-5 p-0 text-green-400 hover:text-green-300 flex-shrink-0"
                                    >
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => { e.stopPropagation(); setEditingSceneId(null); setEditingSceneName(""); }}
                                      className="w-5 h-5 p-0 text-red-400 hover:text-red-300 flex-shrink-0"
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <p 
                                      className="text-white font-medium text-xs truncate"
                                      title={scene.name.length > 15 ? scene.name : undefined}
                                    >
                                      {scene.name.length > 15 ? `${scene.name.substring(0, 15)}...` : scene.name}
                                    </p>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        setEditingSceneId(scene.id); 
                                        setEditingSceneName(scene.name); 
                                      }}
                                      className="w-5 h-5 p-0 text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100 flex-shrink-0"
                                      data-testid={`rename-scene-${index}`}
                                    >
                                      <Edit3 className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                                <span className={`status-badge status-${scene.status} text-[9px] px-1 py-0 ml-auto flex-shrink-0`}>
                                  {scene.status}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Bottom row: script excerpt + delete action */}
                          <div className="flex items-center justify-between mt-1.5 pl-[60px]">
                            <p className="text-zinc-500 text-[10px] truncate flex-1 mr-2">
                              {scene.script_segment?.substring(0, 40)}...
                            </p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleDeleteScene(scene.id); }}
                                className="text-zinc-400 hover:text-red-400 w-6 h-6 bg-zinc-700/50"
                                data-testid={`delete-scene-${index}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Import Assets Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-indigo-400" />
              Import Assets from Another Project
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {!selectedSourceProject ? (
              /* Step 1: Select source project */
              <div>
                <p className="text-sm text-zinc-400 mb-4">Select a project to import assets from:</p>
                {importLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : importableProjects.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No other projects found</p>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2 pr-4">
                      {importableProjects.map((proj) => (
                        <div
                          key={proj.id}
                          onClick={() => handleSelectSourceProject(proj)}
                          className="p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 cursor-pointer border border-zinc-700 hover:border-indigo-500 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-white font-medium">{proj.name}</span>
                            <span className="text-zinc-500 text-sm">{proj.asset_count} assets</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            ) : (
              /* Step 2: Select assets to import */
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-zinc-400">Importing from:</p>
                    <p className="text-white font-medium">{selectedSourceProject.name}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedSourceProject(null);
                      setSourceAssets([]);
                      setSelectedImportAssets([]);
                    }}
                    className="text-zinc-400"
                  >
                    Change Project
                  </Button>
                </div>

                {importLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : sourceAssets.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No assets in this project</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={selectAllImportAssets}
                        className="text-indigo-400 text-xs"
                      >
                        {selectedImportAssets.length === sourceAssets.length ? "Deselect All" : "Select All"}
                      </Button>
                      <span className="text-zinc-500 text-sm">
                        {selectedImportAssets.length} / {sourceAssets.length} selected
                      </span>
                    </div>

                    <ScrollArea className="h-[250px]">
                      <div className="space-y-2 pr-4">
                        {sourceAssets.map((asset) => (
                          <div
                            key={asset.id}
                            onClick={() => toggleImportAsset(asset.id)}
                            className={`p-2 rounded-lg cursor-pointer border transition-colors flex items-center gap-3 ${
                              selectedImportAssets.includes(asset.id)
                                ? "bg-indigo-500/20 border-indigo-500"
                                : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedImportAssets.includes(asset.id)}
                              onChange={() => {}}
                              className="w-4 h-4 rounded border-zinc-600"
                            />
                            {asset.type === "character" || asset.type === "background" || asset.type === "prop" ? (
                              <img
                                src={getAssetUrl(asset.file_url)}
                                alt={asset.name}
                                className="w-10 h-10 rounded object-cover bg-zinc-700"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-zinc-700 flex items-center justify-center">
                                {asset.type === "voice" && <Mic className="w-4 h-4 text-zinc-400" />}
                                {asset.type === "music" && <Music className="w-4 h-4 text-zinc-400" />}
                                {asset.type === "motion" && <Video className="w-4 h-4 text-zinc-400" />}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm truncate">{asset.name}</p>
                              <p className="text-zinc-500 text-xs capitalize">{asset.type}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">Cancel</Button>
            </DialogClose>
            {selectedSourceProject && sourceAssets.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleImportAssets(true)}
                  disabled={importing}
                  className="border-zinc-700 text-zinc-300"
                >
                  {importing ? "Importing..." : "Import All"}
                </Button>
                <Button
                  onClick={() => handleImportAssets(false)}
                  disabled={importing || selectedImportAssets.length === 0}
                  className="bg-indigo-500 hover:bg-indigo-600"
                >
                  {importing ? "Importing..." : `Import Selected (${selectedImportAssets.length})`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Shot Choice Dialog */}
      <Dialog open={showAddShotChoice} onOpenChange={setShowAddShotChoice}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-500" />
              How do you want to create a shot?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <button
              onClick={() => {
                setShowAddShotChoice(false);
                navigate(`/project/${projectId}/script-tools`);
              }}
              className="w-full flex items-center gap-4 p-4 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-indigo-500/50 transition-all group"
            >
              <div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center group-hover:bg-indigo-500/30 transition-colors">
                <Wand2 className="w-6 h-6 text-indigo-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-medium">Create Full Shot List from Script</p>
                <p className="text-zinc-500 text-sm">Let us analyze your script and generate all shots</p>
              </div>
            </button>
            
            <button
              onClick={() => {
                setShowAddShotChoice(false);
                // Generate default name
                const sortedScenes = [...scenes].sort((a, b) => (a.order || 0) - (b.order || 0));
                const nextNumber = sortedScenes.length + 1;
                setNewShotName(`Shot ${nextNumber}`);
                setNewShotInsertIndex(sortedScenes.length); // Default: after last shot
                setShowCreateShotModal(true);
              }}
              className="w-full flex items-center gap-4 p-4 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-amber-500/50 transition-all group"
            >
              <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                <Plus className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-medium">Create a Shot Manually</p>
                <p className="text-zinc-500 text-sm">Add a single shot and choose where to place it</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Shot Modal */}
      <Dialog 
        open={showCreateShotModal} 
        onOpenChange={(open) => {
          setShowCreateShotModal(open);
          if (!open) {
            setNewShotName("");
            setNewShotInsertIndex(null);
          }
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-500" />
              Create a Shot
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Create a new shot and choose where to insert it in your shot list.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Shot Name Input */}
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm">Shot Name</Label>
              <Input
                value={newShotName}
                onChange={(e) => setNewShotName(e.target.value)}
                placeholder="Enter shot name..."
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="new-shot-name-input"
              />
            </div>
            
            {/* Insertion Position */}
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm">Where do you want to insert this shot?</Label>
              <ScrollArea className="h-[250px] border border-zinc-800 rounded-lg">
                <div className="p-2">
                  {(() => {
                    const sortedScenes = [...scenes].sort((a, b) => (a.order || 0) - (b.order || 0));
                    const items = [];
                    
                    // If no shots exist, show single option
                    if (sortedScenes.length === 0) {
                      items.push(
                        <button
                          key="first-shot"
                          onClick={() => setNewShotInsertIndex(0)}
                          className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                            newShotInsertIndex === 0
                              ? 'bg-amber-500/20 border border-amber-500/50'
                              : 'bg-zinc-800/30 hover:bg-zinc-800/50 border border-transparent'
                          }`}
                        >
                          <div className={`w-3 h-3 rounded-full border-2 ${
                            newShotInsertIndex === 0 ? 'border-amber-500 bg-amber-500' : 'border-zinc-600'
                          }`} />
                          <span className="text-sm text-zinc-400">First shot in the list</span>
                        </button>
                      );
                      return items;
                    }
                    
                    // Add "Before first shot" option
                    items.push(
                      <button
                        key="before-first"
                        onClick={() => setNewShotInsertIndex(0)}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                          newShotInsertIndex === 0
                            ? 'bg-amber-500/20 border border-amber-500/50'
                            : 'bg-zinc-800/30 hover:bg-zinc-800/50 border border-transparent'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full border-2 ${
                          newShotInsertIndex === 0 ? 'border-amber-500 bg-amber-500' : 'border-zinc-600'
                        }`} />
                        <span className="text-sm text-zinc-400">Before first shot</span>
                      </button>
                    );
                    
                    // Add each shot with "After Shot X" option
                    sortedScenes.forEach((s, idx) => {
                      const shotName = s.name || `Shot ${idx + 1}`;
                      const displayName = shotName.length > 35 ? shotName.substring(0, 35) + '...' : shotName;
                      
                      // Shot display (not clickable, just for context)
                      items.push(
                        <div
                          key={`shot-${s.id}`}
                          className="w-full flex items-center gap-3 p-2 rounded-lg bg-zinc-800/20"
                        >
                          <div className="w-10 h-7 rounded bg-zinc-700 overflow-hidden shrink-0">
                            {s.composite_image_url || s.preview_url ? (
                              <img 
                                src={getAssetUrl(s.composite_image_url || s.preview_url)} 
                                alt="" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film className="w-3 h-3 text-zinc-600" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                              {displayName}
                            </div>
                          </div>
                        </div>
                      );
                      
                      // "After this shot" insertion point
                      items.push(
                        <button
                          key={`after-${s.id}`}
                          onClick={() => setNewShotInsertIndex(idx + 1)}
                          className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                            newShotInsertIndex === idx + 1
                              ? 'bg-amber-500/20 border border-amber-500/50'
                              : 'bg-zinc-800/30 hover:bg-zinc-800/50 border border-transparent'
                          }`}
                        >
                          <div className={`w-3 h-3 rounded-full border-2 ${
                            newShotInsertIndex === idx + 1 ? 'border-amber-500 bg-amber-500' : 'border-zinc-600'
                          }`} />
                          <span className="text-sm text-zinc-400">
                            {idx === sortedScenes.length - 1 ? 'After last shot' : `After ${displayName.substring(0, 20)}${displayName.length > 20 ? '...' : ''}`}
                          </span>
                        </button>
                      );
                    });
                    
                    return items;
                  })()}
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => setShowCreateShotModal(false)} 
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateShot}
              disabled={creatingShotLoading || !newShotName.trim() || newShotInsertIndex === null}
              className="bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="create-shot-confirm-btn"
            >
              {creatingShotLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Shot
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* File Upload Progress Modal */}
      {fileUploadProgress && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-80 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{fileUploadProgress.filename}</p>
                <p className="text-xs text-zinc-400 capitalize">
                  {fileUploadProgress.stage === 'uploading' && 'Uploading...'}
                  {fileUploadProgress.stage === 'processing' && 'Processing file...'}
                </p>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                style={{ width: `${fileUploadProgress.progress}%` }}
              />
            </div>
            <p className="text-xs text-zinc-500 text-center mt-2">{fileUploadProgress.progress}%</p>
          </div>
        </div>
      )}
    </div>
  );
}
